import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { RefreshCw, TrendingUp, Sparkles, Trophy } from "lucide-react";

const BET_TYPES = [
  { value: "mixed", label: "Vegyes (legjobb)" },
  { value: "over_2_5", label: "2,5 gól felett" },
  { value: "over_3_5", label: "3,5 gól felett" },
  { value: "btts", label: "Mindkét csapat szerez gólt" },
  { value: "btts_both_halves", label: "BTTS mindkét félidőben" },
];

const formatTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" });
};

const formatPct = (p) => (p == null ? "—" : `${(p * 100).toFixed(1)}%`);

export default function Slips() {
  const [betType, setBetType] = useState("mixed");
  const [minProb, setMinProb] = useState(55);
  const [slipSize, setSlipSize] = useState(3);
  const [numSlips, setNumSlips] = useState(5);
  const [stake, setStake] = useState(1000);
  const [hoursAhead, setHoursAhead] = useState(6);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/slips", {
        params: {
          bet_type: betType, min_prob: minProb / 100,
          slip_size: slipSize, num_slips: numSlips,
          hours_ahead: hoursAhead, refresh,
        },
      });
      setData(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betType, slipSize, numSlips, minProb, hoursAhead]);

  return (
    <Layout>
      <div className="px-8 py-6">
        <div className="flex items-end justify-between mb-6 pb-5 border-b border-white/10">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">Generálás</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Szelvény Ötletek</h1>
            <p className="text-sm text-zinc-400 mt-1">
              Automatikusan kombinált szelvények a kiválasztott szűrők alapján.
            </p>
          </div>
          <Button
            onClick={() => load(true)}
            disabled={loading}
            data-testid="refresh-slips-btn"
            variant="outline"
            className="border-white/15 hover:bg-white/5 text-zinc-200"
          >
            <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            Frissítés
          </Button>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-white/10 border border-white/10 mb-6">
          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-2">Időablak</div>
            <Select value={String(hoursAhead)} onValueChange={(v) => setHoursAhead(parseInt(v))}>
              <SelectTrigger data-testid="slips-hours-ahead-select" className="bg-[#141414] border-white/10 h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                {[1, 2, 3, 6, 12, 24, 48].map((h) => (
                  <SelectItem key={h} value={String(h)} data-testid={`slips-hours-ahead-${h}`}>
                    Köv. {h} óra
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-2">Szelvénytípus</div>
            <Select value={betType} onValueChange={setBetType}>
              <SelectTrigger data-testid="bet-type-select" className="bg-[#141414] border-white/10 h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                {BET_TYPES.map((b) => (
                  <SelectItem key={b.value} value={b.value} data-testid={`bet-type-${b.value}`}>
                    {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-[#0A0A0A] p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Min. esély</div>
              <div className="text-xs font-mono text-[#22C55E]">{minProb}%</div>
            </div>
            <Slider
              data-testid="min-prob-slider"
              value={[minProb]}
              onValueChange={([v]) => setMinProb(v)}
              min={0}
              max={95}
              step={1}
              className="mt-2"
            />
          </div>

          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-2">Meccs/szelvény</div>
            <Select value={String(slipSize)} onValueChange={(v) => setSlipSize(parseInt(v))}>
              <SelectTrigger data-testid="slip-size-select" className="bg-[#141414] border-white/10 h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                {[2, 3, 4, 5, 6].map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`slip-size-${n}`}>
                    {n} meccs
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-2">Hány szelvény</div>
            <Select value={String(numSlips)} onValueChange={(v) => setNumSlips(parseInt(v))}>
              <SelectTrigger data-testid="num-slips-select" className="bg-[#141414] border-white/10 h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                {[1, 3, 5, 8, 10].map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`num-slips-${n}`}>
                    {n} db
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-[#0A0A0A] p-4">
            <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-2">Tét (Ft)</div>
            <Input
              type="number"
              value={stake}
              onChange={(e) => setStake(parseFloat(e.target.value) || 0)}
              data-testid="stake-input"
              className="bg-[#141414] border-white/10 h-9 font-mono"
              min={0}
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 border border-[#EF4444]/30 bg-[#EF4444]/10 text-sm text-[#EF4444]">
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="text-center py-16 text-zinc-500">Adatok lekérése...</div>
        )}

        {data && data.slips.length === 0 && (
          <div className="text-center py-16 border border-dashed border-white/10">
            <Sparkles className="mx-auto text-zinc-600 mb-3" size={28} />
            <div className="text-zinc-400">Nincs a feltételeknek megfelelő szelvény.</div>
            <div className="text-sm text-zinc-500 mt-1">Próbáld csökkenteni a minimum esélyt vagy váltani szelvénytípust.</div>
          </div>
        )}

        {/* Slips */}
        <div className="space-y-5">
          {data?.slips?.map((slip, idx) => {
            const payout = slip.combined_odds * stake;
            const profit = payout - stake;
            return (
              <div
                key={slip.slip_id}
                data-testid={`slip-card-${idx}`}
                className="border border-white/10 bg-[#0A0A0A] card-hover"
              >
                {/* Slip header */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-white/[0.02]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#007AFF]/10 border border-[#007AFF]/30 flex items-center justify-center text-[#007AFF] font-display font-bold text-sm">
                      #{idx + 1}
                    </div>
                    <div>
                      <div className="font-display font-bold text-lg tracking-tight">
                        {slip.picks.length} meccses szelvény
                      </div>
                      <div className="text-xs text-zinc-500">
                        Kombinált esély: <span className="font-mono text-[#22C55E]">{formatPct(slip.combined_prob)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-px bg-white/10">
                    <div className="bg-[#0A0A0A] px-4 py-2">
                      <div className="text-[10px] tracking-wider text-zinc-500 uppercase">Szorzó</div>
                      <div className="font-mono text-xl font-bold text-white" data-testid={`slip-${idx}-odds`}>
                        {slip.combined_odds.toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-[#0A0A0A] px-4 py-2">
                      <div className="text-[10px] tracking-wider text-zinc-500 uppercase">Várható nyeremény</div>
                      <div className="font-mono text-xl font-bold text-[#22C55E]" data-testid={`slip-${idx}-payout`}>
                        {payout.toLocaleString("hu-HU", { maximumFractionDigits: 0 })} Ft
                      </div>
                    </div>
                    <div className="bg-[#0A0A0A] px-4 py-2">
                      <div className="text-[10px] tracking-wider text-zinc-500 uppercase">Nyereség</div>
                      <div className="font-mono text-xl font-bold text-[#007AFF]">
                        +{profit.toLocaleString("hu-HU", { maximumFractionDigits: 0 })} Ft
                      </div>
                    </div>
                  </div>
                </div>

                {/* Picks */}
                <div className="divide-y divide-white/5">
                  {slip.picks.map((p, i) => {
                    const isValue = p.value > 0.05;
                    return (
                      <div
                        key={`${slip.slip_id}-${p.match_id}-${i}`}
                        className="px-5 py-3 grid grid-cols-12 gap-3 items-center hover:bg-white/[0.02]"
                      >
                        <div className="col-span-1 text-xs font-mono text-zinc-500">
                          {formatTime(p.start_timestamp)}
                        </div>
                        <div className="col-span-5">
                          <div className="text-sm font-medium">
                            {p.home_team} <span className="text-zinc-500">vs</span> {p.away_team}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5">{p.tournament}</div>
                        </div>
                        <div className="col-span-3">
                          <Badge variant="outline" className="border-[#007AFF]/30 text-[#3395FF] bg-[#007AFF]/5 font-medium">
                            {p.market_label}
                          </Badge>
                        </div>
                        <div className="col-span-1 text-right">
                          <div className="font-mono text-sm font-bold text-[#22C55E]">
                            {formatPct(p.prob)}
                          </div>
                        </div>
                        <div className="col-span-1 text-right">
                          <div className="font-mono text-sm font-bold">{p.odds.toFixed(2)}</div>
                        </div>
                        <div className="col-span-1 text-right">
                          {isValue && (
                            <Badge className="bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30 hover:bg-[#22C55E]/20">
                              <TrendingUp size={10} className="mr-1" /> VALUE
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {data && (
          <div className="mt-8 text-xs text-zinc-500 flex items-center gap-2">
            <Trophy size={12} />
            Összes találat: <span className="font-mono text-zinc-400">{data.total_picks}</span> · Frissítve:{" "}
            <span className="font-mono text-zinc-400">{new Date(data.fetched_at).toLocaleTimeString("hu-HU")}</span>
          </div>
        )}
      </div>
    </Layout>
  );
}
